import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, ShieldCheck, Trash2, Archive, UserX } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { useSEO } from '@/lib/useSEO';

export const SUPPORT_EMAIL = 'automarketcolombia1@gmail.com';
const RETENTION = 'durante el tiempo exigido por la normativa aplicable';

const Block = ({ icon: Icon, title, items, testid }) => (
  <div className="border border-slate-200 bg-white p-5" data-testid={testid}>
    <h2 className="font-display font-bold text-slate-900 text-sm uppercase tracking-wider flex items-center gap-2">
      <Icon className="h-4 w-4 text-orange-600" /> {title}
    </h2>
    <ul className="mt-3 list-disc pl-5 text-sm text-slate-700 space-y-1.5">
      {items.map((i) => <li key={i}>{i}</li>)}
    </ul>
  </div>
);

const RequestForm = () => {
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [website, setWebsite] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post('/account-deletion/request', { email, reason: reason || undefined, website });
      setSent(data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No pudimos registrar la solicitud. Intenta más tarde.');
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <div className="border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-900" data-testid="delete-request-success">
        {sent}
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="border border-slate-200 bg-white p-5 space-y-3" data-testid="delete-request-form">
      <label className="block text-xs font-bold uppercase tracking-wider text-slate-600" htmlFor="del-email">
        Correo de tu cuenta
      </label>
      <Input id="del-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
             placeholder="tucorreo@ejemplo.com" className="rounded-none h-11" data-testid="delete-request-email-input" />
      <label className="block text-xs font-bold uppercase tracking-wider text-slate-600" htmlFor="del-reason">
        Motivo (opcional)
      </label>
      <Textarea id="del-reason" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)}
                className="rounded-none" rows={3} data-testid="delete-request-reason-input" />
      <input type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)}
             className="hidden" aria-hidden="true" name="website" />
      <p className="text-xs text-slate-500">
        Te enviaremos un enlace al correo de la cuenta para confirmar que eres el titular. Sin esa confirmación no se elimina nada.
      </p>
      <Button type="submit" disabled={busy} data-testid="delete-request-submit-btn"
              className="w-full sm:w-auto rounded-none h-11 px-6 bg-slate-900 hover:bg-slate-800 font-bold">
        {busy ? 'Enviando…' : 'Solicitar eliminación'}
      </Button>
    </form>
  );
};

export default function EliminarCuenta() {
  useSEO({
    title: 'Eliminar mi cuenta | AutoMarket Colombia',
    description: 'Solicita la eliminación de tu cuenta de AutoMarket Colombia y conoce qué datos se eliminan o conservan.',
    canonical: '/eliminar-cuenta',
  });
  return (
    <Layout>
      <div className="mx-auto max-w-5xl px-4 py-10" data-testid="delete-account-public-page">
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-orange-600">AutoMarket Colombia</div>
        <h1 className="mt-2 font-display font-bold text-4xl sm:text-5xl text-slate-900">Eliminar mi cuenta</h1>
        <p className="mt-4 max-w-2xl text-sm text-slate-700">
          Esta página explica cómo solicitar la eliminación de tu cuenta de la aplicación <strong>AutoMarket Colombia</strong>
          {' '}(web, Android e iOS) y de tus datos personales, conforme a la Ley 1581 de 2012 de protección de datos personales.
        </p>

        <div className="mt-8 grid gap-6 lg:grid-cols-[1.1fr_1fr]">
          <div className="space-y-4">
            <h2 className="font-display font-bold text-slate-900 text-base uppercase tracking-wider">Opción 1 · Formulario</h2>
            <RequestForm />
          </div>
          <div className="space-y-4">
            <h2 className="font-display font-bold text-slate-900 text-base uppercase tracking-wider">Opción 2 · Desde tu cuenta</h2>
            <div className="border border-slate-200 bg-white p-5 text-sm text-slate-700" data-testid="delete-account-steps">
              Inicia sesión, ve a <Link to="/perfil" className="underline font-semibold">Mi cuenta</Link> → «Eliminar cuenta» y confirma con tu contraseña.
            </div>
            <h2 className="font-display font-bold text-slate-900 text-base uppercase tracking-wider">Opción 3 · Por correo</h2>
            <div className="border border-slate-200 bg-white p-5 text-sm text-slate-700" data-testid="delete-account-email-option">
              <Mail className="inline h-4 w-4 mr-1 text-orange-600" />
              Escribe a <a href={`mailto:${SUPPORT_EMAIL}?subject=Solicitud%20de%20eliminaci%C3%B3n%20de%20cuenta`}
                           className="underline font-semibold" data-testid="delete-account-support-email">{SUPPORT_EMAIL}</a>
              {' '}desde el correo registrado en tu cuenta, con el asunto «Solicitud de eliminación de cuenta».
            </div>
          </div>
        </div>

        <div className="mt-8 border-l-4 border-orange-500 bg-orange-50 p-4 text-sm text-slate-800" data-testid="delete-account-process">
          <ShieldCheck className="inline h-4 w-4 mr-1" />
          <strong>Cómo procesamos tu solicitud:</strong> primero verificamos que eres el titular de la cuenta. Luego nuestro equipo
          la revisa; si tienes pedidos en curso, PQR abiertas o saldo pendiente como vendedor, la solicitud queda en espera hasta
          resolverlos. Te contactaremos por el correo de tu cuenta si necesitamos información adicional.
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <Block testid="delete-account-deleted" icon={Trash2} title="Datos que se eliminan" items={[
            'Sesiones activas y accesos vinculados (Google / Apple).',
            'Favoritos, lista de deseos y tiendas seguidas.',
            'Notificaciones dentro de la aplicación.',
          ]} />
          <Block testid="delete-account-anonymized" icon={UserX} title="Datos que se anonimizan" items={[
            'Nombre, correo, teléfono, ciudad, foto de perfil y contraseña.',
            'Vendedores: contacto, cuenta bancaria, representante legal y documentos; las publicaciones se desactivan.',
            'Tu nombre en reseñas se reemplaza por «Usuario eliminado».',
          ]} />
          <Block testid="delete-account-retained" icon={Archive} title="Datos que se conservan" items={[
            'Pedidos, pagos, reembolsos, facturas electrónicas y notas crédito.',
            'Pagos y retiros a vendedores; razón social y NIT asociados a facturas.',
            'Aceptación de términos, PQR, mensajes y registros de auditoría.',
            `Se conservan ${RETENTION} (obligaciones legales, tributarias y contables) y luego se eliminan o anonimizan.`,
          ]} />
        </div>

        <p className="mt-6 text-sm text-slate-700">
          Más detalles en nuestra <Link to="/privacidad" className="underline" data-testid="delete-account-privacy-link">Política de Tratamiento de Datos Personales</Link>.
        </p>
      </div>
    </Layout>
  );
}
